import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps, Switch, withRouter } from 'react-router-dom';
import loadable from '@loadable/component';
import LayoutManager from '@managers/LayoutManager';

import Loading from '@components/Loading';
import PrivateRoute from '@components/PrivateRoute';

import { fetchAgencyPublicInfo } from '@actions/agency';
import { fetchDashboardData } from '@actions/dashboard';
import { fetchEntityIcons } from '@actions/entitySection';
import { fetchAgencyProducts } from '@actions/product';
import { fetchProductLayout } from '@actions/productLayout';
import { fetchUserPreferences } from '@actions/user';
import { fetchConfigViolationTypes } from '@actions/violationType';

import { appURLs, getLandingPagePartialURL, pageTitle } from '@utils/AppUtil';
import { hasCEExecutiveReportsAccess, hasCRMExecutiveReportsAccess } from '@utils/AuthUtil';

import AgencyHeader from '../AgencyHeader';

const CaseDetailsPage = loadable(
  () =>
    import(
      /* webpackChunkName: "cases" */
      '@containers/cases/Cases'
    ),
  {
    fallback: <Loading show />,
  }
);
const Dashboard = loadable(
  () =>
    import(
      /* webpackChunkName: "dashboard" */
      '@containers/common/Dashboard'
    ),
  {
    fallback: <Loading show />,
  }
);

const Profile = loadable(
  () =>
    import(
      /* webpackChunkName: "profile" */
      '@containers/common/Profile'
    ),
  {
    fallback: <Loading show />,
  }
);
const AgencySetup = loadable(
  () =>
    import(
      /* webpackChunkName: "setup" */
      '@containers/config/AgencySetup'
    ),
  {
    fallback: <Loading show />,
  }
);
const CustomerSubmissionDetails = loadable(
  () =>
    import(
      /* webpackChunkName: "csdp" */
      '@containers/crm/CustomerSubmissions/CustomerSubmissionDetails'
    ),
  {
    fallback: <Loading show />,
  }
);
const CreateCustomerSubmission = loadable(
  () =>
    import(
      /* webpackChunkName: "csp" */
      '@containers/crm/CreateCustomerSubmission/CreateCustomerSubmission'
    ),
  {
    fallback: <Loading show />,
  }
);

const ActionCenter = loadable(
  () =>
    import(
      /* webpackChunkName: "actionCenter" */
      '@containers/crm/ActionCenter'
    ),
  {
    fallback: <Loading show />,
  }
);

const CaseListing = loadable(
  () =>
    import(
      /* webpackChunkName: "clp" */
      '../AgencyNavigation/CaseListing'
    ),
  {
    fallback: <Loading show />,
  }
);
const CustomerSubmissionListing = loadable(
  () =>
    import(
      /* webpackChunkName: "cslp" */
      '../AgencyNavigation/CustomerSubmissionListing/CustomerSubmissionListing'
    ),
  {
    fallback: <Loading show />,
  }
);
const Reports = loadable(
  () =>
    import(
      /* webpackChunkName: "reports" */
      '../AgencyNavigation/Reports'
    ),
  {
    fallback: <Loading show />,
  }
);

const ContactListing = loadable(
  () =>
    import(
      /* webpackChunkName: "contacts" */
      '../AgencyNavigation/ContactListing/ContactListing'
    ),
  {
    fallback: <Loading show />,
  }
);

const CommLink = loadable(
  () =>
    import(
      /* webpackChunkName: "commlink" */
      '../AgencyNavigation/CommLink/CommLink'
    ),
  {
    fallback: <Loading show />,
  }
);

const FinanceSetup = loadable(
  () =>
    import(
      /* webpackChunkName: "finance" */
      '../AgencyNavigation/Finance/FinanceSetup'
    ),
  {
    fallback: <Loading show />,
  }
);

const EntityListing = loadable(
  () =>
    import(
      /* webpackChunkName: "entityListing" */
      '../AgencyNavigation/EntityListing/EntityListing'
    ),
  {
    fallback: <Loading show />,
  }
);

const ExecutiveReports = loadable(
  () =>
    import(
      /* webpackChunkName: "executiveReport" */
      '../AgencyNavigation/ExecutiveReports'
    ),
  {
    fallback: <Loading show />,
  }
);

interface IHomeProps extends RouteComponentProps<{}> {
  // from redux
  loadingComponents: number;
  fetchAgencyProducts: TFetchAgencyProducts;
  userProfile: IUserProfile;
  hasProductCEAccess: boolean;
  hasProductCRMAccess: boolean;
  fetchProductLayout: TFetchProductLayout;
  fetchUserPreferences: TFetchUserPreferences;
  fetchEntityIcons: (agencyId: number) => void;
  fetchConfigViolationTypes: TFetchConfigViolationTypes;
  fetchAgencyPublicInfo: TFetchAgencyPublicInfo;
  fetchDashboardData: TFetchDashboardData;
}

interface IHomeState {
  isLayoutFetching: boolean;
}

class Home extends React.Component<IHomeProps, IHomeState> {
  constructor(props: IHomeProps) {
    super(props);
    this.state = {
      isLayoutFetching: true,
    };
  }

  async componentDidMount() {
    this.prefetchComponents();

    if (!LayoutManager.isProductLayoutPresent()) {
      this.setState({
        isLayoutFetching: true,
      });

      const { userProfile, hasProductCEAccess, hasProductCRMAccess } = this.props;
      const dashboardParams = {
        productType: hasProductCEAccess ? ConfigProductType.XERCE : ConfigProductType.CRM,
        dashboardAccess: hasProductCEAccess
          ? hasProductCRMAccess
            ? DashboardAccess.ALL_STAFF_DASHBOARD
            : userProfile?.scopes?.xerce?.dashboardAccess
          : DashboardAccess.SELF_DASHBOARD,
      };

      await LayoutManager.refreshLayout([
        this.props.fetchProductLayout,
        this.props.fetchAgencyProducts,
        this.props.fetchUserPreferences,
        () => this.props.fetchAgencyPublicInfo(userProfile.agencyId),
        () => this.props.fetchDashboardData(dashboardParams),
        ...(hasProductCEAccess
          ? [this.props.fetchConfigViolationTypes, () => this.props.fetchEntityIcons(userProfile.agencyId)]
          : []),
      ]).finally(() => {
        this.setState({
          isLayoutFetching: false,
        });
      });
    }
  }

  prefetchComponents = () => {
    Dashboard.preload();
    CaseListing.preload();
    CaseDetailsPage.preload();
    CustomerSubmissionListing.preload();
    CustomerSubmissionDetails.preload();
  };

  getAgencySettingsAppendedURL = (partialURL: string) => `${appURLs.agencySetupPartialURL}/${partialURL}`;

  getProductSettingsAppendedURL = (partialURL: string) => `${appURLs.productSetupPartialURL}/${partialURL}`;

  render() {
    if (this.state.isLayoutFetching) {
      return <Loading show />;
    }

    const { userProfile } = this.props;
    const agencyId = userProfile ? userProfile.agencyId : null;
    const agencyURL = '/agencies/:agencyId';

    return (
      <div className="app">
        <header className="app__header">
          <AgencyHeader />
        </header>
        <main className="app__contents">
          <Switch>
            <PrivateRoute
              exact
              path={`${agencyURL}/${appURLs.dashboardPartialURL}`}
              title={pageTitle.dashboard}
              component={Dashboard}
              acl={ACL.PRODUCT}
            />
            <PrivateRoute
              exact
              path={`${agencyURL}/${appURLs.reportsPartialURL}`}
              title={pageTitle.reports}
              component={
                hasCEExecutiveReportsAccess(userProfile) || hasCRMExecutiveReportsAccess(userProfile)
                  ? ExecutiveReports
                  : Reports
              }
              acl={ACL.PRODUCT}
            />
            <PrivateRoute
              exact
              path={`${agencyURL}/${appURLs.casesPartialURL}`}
              component={CaseListing}
              title={pageTitle.cases}
              acl={ACL.PRODUCT}
            />
            <PrivateRoute
              exact
              path={`${agencyURL}/${appURLs.customerSubmissionsPartialURL}`}
              component={CustomerSubmissionListing}
              title={pageTitle.customerSubmissions}
              acl={ACL.CRM}
            />
            <PrivateRoute
              path={`${agencyURL}/${appURLs.setupPartialURL}`}
              title={pageTitle.setup}
              component={AgencySetup}
            />
            <PrivateRoute
              exact
              path={`${agencyURL}/${appURLs.contactsPartialURL}`}
              component={ContactListing}
              title={pageTitle.contactsPage}
            />
            <PrivateRoute
              exact
              path={`${agencyURL}/${appURLs.commlinkPartialURL}`}
              component={CommLink}
              title={pageTitle.commlinkPage}
              acl={ACL.CRM}
            />
            <PrivateRoute
              path={`${agencyURL}/${appURLs.financePartialURL}`}
              component={FinanceSetup}
              title={pageTitle.financePage}
            />
            <PrivateRoute
              exact
              // tslint:disable-next-line: max-line-length
              path={`${agencyURL}/${appURLs.entitiesPartialURL}/:configViolationTypeId`}
              component={EntityListing}
              title={pageTitle.contactsPage}
            />
            <PrivateRoute
              path={`${agencyURL}/${appURLs.casePartialURL}`}
              component={CaseDetailsPage}
              acl={ACL.PRODUCT}
            />
            <PrivateRoute
              exact
              // tslint:disable-next-line:max-line-length
              path={`${agencyURL}/${appURLs.createCustomerSubmissionPartialURL}`}
              title={pageTitle.createCustomerSubmission}
              component={CreateCustomerSubmission}
            />
            <PrivateRoute
              exact
              // tslint:disable-next-line:max-line-length
              path={`${agencyURL}/${appURLs.actionCenterPartialURL}`}
              title={pageTitle.createCustomerSubmission}
              component={ActionCenter}
            />
            <PrivateRoute
              exact
              // tslint:disable-next-line:max-line-length
              path={`${agencyURL}/${appURLs.customerSubmissionPartialURL}/:customerSubmissionId`}
              title={pageTitle.customerSubmissionDetailPage}
              component={CustomerSubmissionDetails}
            />
            <PrivateRoute
              path={`${agencyURL}/${appURLs.profilePartialURL}`}
              title={pageTitle.profile}
              component={Profile}
            />
            <Redirect to={`/agencies/${agencyId}/${getLandingPagePartialURL(userProfile)}`} />
          </Switch>
          <Loading show={this.props.loadingComponents > 0} onHide={(e) => null} />
        </main>
      </div>
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  loadingComponents: state.appState.loadingComponents,
  userProfile: state.auth.userProfile,
  hasProductCEAccess: state.auth.hasProductCEAccess,
  hasProductCRMAccess: state.auth.hasProductCRMAccess,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAgencyProducts: () => dispatch(fetchAgencyProducts()),
  fetchProductLayout: () => dispatch(fetchProductLayout()),
  fetchUserPreferences: () => dispatch(fetchUserPreferences(true)),
  fetchEntityIcons: (agencyId) => dispatch(fetchEntityIcons(agencyId)),
  fetchConfigViolationTypes: () => dispatch(fetchConfigViolationTypes(true)),
  fetchAgencyPublicInfo: (agencyId: number) => dispatch(fetchAgencyPublicInfo(agencyId, true)),
  fetchDashboardData: (dashboardAccess) => dispatch(fetchDashboardData(dashboardAccess)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Home));
